import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Table, Input, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

interface DataType {
  id?: number;
  key: string;
  name: string;
  parent_id?: number;
  isParent?: boolean;
  ownUsage?: number;
  total?: number;
  [key: string]: string | number | boolean | undefined;
}

interface LayerFeedTableProps {
  data: any[];
  formType: string;
  monthHeaders: string[];
  existingData?: DataType[] | null;
  readonly?: boolean;
}

const LayerFeedTable = forwardRef<{ getTableData: () => DataType[] }, LayerFeedTableProps>(
  ({ data, formType, monthHeaders, existingData, readonly = false }, ref) => {
    const [tableData, setTableData] = useState<DataType[]>([]);

    useEffect(() => {
      if (tableData.length === 0) {
        // Only set the table data when it's empty (initial load)
        let initialData = formatData(data, monthHeaders);
        if (existingData && existingData.length > 0) {
          initialData = mergeExistingData(initialData, existingData);
        }
        setTableData(initialData);
      }
    }, [data, monthHeaders, existingData]);

    // Merge existing data with formatted data
    const mergeExistingData = (formattedData: DataType[], existingData: DataType[]): DataType[] => {
      return formattedData.map((row) => {
        // Try to find matching existing data by id
        const existingRow = existingData.find((existing) => existing.id === row.id);

        if (existingRow) {
          // Merge the existing data with our formatted row
          return {
            ...row,
            ...existingRow,
            // We need to keep the key and name from our formatted data
            key: row.key,
            name: row.name,
            isParent: row.isParent,
            parent_id: row.parent_id,
          };
        }

        return row;
      });
    };

    // Expose the table data through ref
    useImperativeHandle(ref, () => ({
      getTableData: () => tableData,
    }));

    const handleInputChange = (value: number, rowKey: string, column: string) => {
      if (readonly) return; // Don't update if in readonly mode

      const sanitizedValue = Math.max(0, value); // Prevent negative values

      setTableData((prevData) => {
        return prevData.map((row) => {
          // Only update this specific row using the exact row key
          if (row.key === rowKey) {
            const updatedRow = {
              ...row,
              [column]: sanitizedValue,
            };

            // Recalculate the total only for this specific row
            if (monthHeaders.includes(column)) {
              const total = monthHeaders.reduce((sum, month) => sum + (Number(updatedRow[month]) || 0), 0);
              updatedRow.total = total;
            }

            return updatedRow;
          }

          // Leave all other rows completely unchanged
          return row;
        });
      });
    };

    const columns: ColumnsType<DataType> = [
      {
        title: formType,
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: DataType) => (
          <span
            style={{
              fontWeight: record.isParent ? 'bold' : 'normal',
              fontSize: '14px',
            }}
          >
            {text}
          </span>
        ),
      },
      ...monthHeaders.map((month) => ({
        title: month,
        dataIndex: month,
        key: month,
        render: (_: any, record: DataType) =>
          readonly ? (
            <span>{record[month] ?? 0}</span>
          ) : (
            <Input
              type="number"
              min={0}
              value={
                typeof record[month] === 'number'
                  ? (record[month] as number)
                  : typeof record[month] === 'string'
                  ? (record[month] as string)
                  : undefined
              }
              onChange={(e) => handleInputChange(Number(e.target.value), record.key, month)}
              style={{ width: 80, height: 28, marginLeft: 8 }}
              disabled={readonly}
            />
          ),
      })),
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        render: (_, record) => <span>{record.total ?? 0}</span>,
      },
      {
        title: 'Own Usage',
        dataIndex: 'ownUsage',
        key: 'ownUsage',
        render: (_, record: DataType) =>
          readonly ? (
            <span>{record.ownUsage ?? 0}</span>
          ) : (
            <Input
              type="number"
              min={0}
              value={record.ownUsage ?? 0}
              onChange={(e) => handleInputChange(Number(e.target.value), record.key, 'ownUsage')}
              style={{ width: 80, height: 28, marginLeft: 8 }}
              disabled={readonly}
            />
          ),
      },
    ];

    return (
      <div>
        <Typography.Title level={4} style={{ textAlign: 'center' }}>
          Poultry Feed Production Forecast (MT) - {formType}
        </Typography.Title>

        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          size="small"
          scroll={{ x: true, scrollToFirstRowOnChange: true }}
        />
      </div>
    );
  },
);

// Helper function to format data before rendering
const formatData = (feedChildren: any[], monthHeaders: string[]): DataType[] => {
  let keyCounter = 1;

  const flattenChildren = (items: any[], isRoot = false): DataType[] => {
    return items.flatMap((parent) => {
      const parentKey = `parent-${parent.id}-${keyCounter}`;

      const parentRow: DataType = {
        key: parentKey,
        id: parent.id,
        name: parent.type,
        isParent: true,
        ...monthHeaders.reduce((acc, month) => ({ ...acc, [month]: 0 }), {}),
        ownUsage: 0,
        total: 0,
      };

      keyCounter++;

      // If the parent has children, add them after the parent
      const childRows = parent.children
        ? parent.children.map((child: any) => {
            const childKey = `child-${child.id}-${keyCounter++}`;
            return {
              key: childKey, // Ensure each child has a truly unique key
              id: child.id,
              parent_id: parent.id,
              name: child.type,
              ...monthHeaders.reduce((acc, month) => ({ ...acc, [month]: 0 }), {}),
              ownUsage: 0,
              total: 0,
            };
          })
        : [];

      return [parentRow, ...childRows];
    });
  };

  return flattenChildren(feedChildren);
};

export default LayerFeedTable;
